'use client';

import { type FormEvent, type ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import type { AreaResponse } from '@/src/lib/api';
import { useContainerWidthAbove, useScrollToViewportTop } from '@/src/lib/hooks/ui';
import { getPriceFiltersCount, getRoomsFiltersCount } from '@/src/lib/filters/filterLabels';
import LocationCombobox from './LocationCombobox';
import RoomsDropdown from './RoomsDropdown';
import PriceDropdown from './PriceDropdown';
import AllFiltersButton from './AllFiltersButton';
import SearchSubmit from './SearchSubmit';
import { type ListingSearchMode } from './useListingSearchState';
import { SearchFiltersProvider, useSearchFilters } from './SearchFiltersProvider';

export type ListingSearchProps = {
  /** Visual size variant. */
  size: 'sm' | 'lg';
  /** Live-vs-navigate state behavior. Defaults: sm → live, lg → navigate. */
  mode?: ListingSearchMode;
  /** IP-derived city used as the "Near Me" default. */
  nearMeArea?: AreaResponse | null;
  /** Optional product action rendered after the All filters control. */
  trailingAction?: ReactNode;
  compactControls?: boolean;
};

export type ListingSearchControlsProps = Pick<ListingSearchProps, 'size' | 'trailingAction' | 'compactControls'> & {
  /**
   * HP-8710. While the location suggestions are open, collapse the trailing
   * controls so Location (and the fluid-width panel it sizes) takes the whole
   * bar, and pin the bar to the top of the viewport so the panel has room to
   * render below the input instead of flipping above it.
   *
   * Opt-in per call site rather than derived from a media query: the only
   * surface that wants this is the homepage hero's mobile copy, and
   * `HomepageListingSearches` already knows which copy that is from the CSS
   * that shows it.
   */
  expandLocationOnOpen?: boolean;
};

function getDefaultMode(size: ListingSearchProps['size']): ListingSearchMode {
  return size === 'lg' ? 'navigate' : 'live';
}

// Container-query thresholds — all measured against the bar's own width
// (containerType: inline-size on the root), not the viewport. Tuned via
// scripts/probe-listing-search-thresholds.mjs.
//
// size="sm" (SRP/HDP header): staged reveal keeps Location ≥ ~50% of the
// bar at every stage.
const SM_ROOMS_INLINE_MIN = '32em';
const SM_PRICE_INLINE_MIN = '48em';
// Figma fixes the desktop SRP location field at 276px; rem preserves that proportion when the root text size changes.
const SM_HEADER_LOCATION_BASIS = '17.25rem';

// size="lg" (homepage hero): Rooms + Price collapse into All filters until
// the full bar has enough container width to remain on one row. Keep this
// below the homepage desktop content box (44em after wrapper padding) so the
// desktop hero reliably shows the primary filters instead of sitting exactly
// on the breakpoint.
const LG_PRIMARY_FILTERS_INLINE_MIN = '42em';

const container = css({
  containerType: 'inline-size',
  containerName: 'listingSearch',
});

/**
 * Unified search component used in the homepage hero (`size="lg"`) and the
 * SRP/HDP header (`size="sm"`). Replaces `HomepageSearchBar` and
 * `HeaderNav/FilterNav` once call sites migrate.
 *
 * Composition: `[Location | Rooms | Price | All filters | Search]`
 *
 * Styling lives inline on Constellation primitives (`Box`, `Divider`,
 * `IconButton`, …) via the `css` prop using Panda tokens only. No
 * standalone styles file; no magic numbers.
 */
export function ListingSearchControls({
  size,
  trailingAction,
  compactControls = false,
  expandLocationOnOpen = false,
}: ListingSearchControlsProps) {
  const tFilters = useTranslations('filters');
  const {
    filters,
    applyFilters,
    area,
    setArea,
    submit,
    searchArea,
    isPending,
    activeFilterCount,
    nearMeArea,
    openAllFilters,
    markUserInteraction,
  } = useSearchFilters();

  const containerRef = useRef<HTMLDivElement>(null);
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);

  // Keyed off the suggestions being on screen rather than input focus on
  // purpose: in navigate mode picking a suggestion only stages the area, so
  // the user still needs Search. Focus survives that tap; the panel does not.
  const expandLocationField = expandLocationOnOpen && suggestionsVisible;

  useScrollToViewportTop(containerRef, expandLocationField);

  const roomsShownInline = useContainerWidthAbove(
    containerRef,
    size === 'lg' ? LG_PRIMARY_FILTERS_INLINE_MIN : SM_ROOMS_INLINE_MIN,
  );
  const priceShownInline = useContainerWidthAbove(
    containerRef,
    size === 'lg' ? LG_PRIMARY_FILTERS_INLINE_MIN : SM_PRICE_INLINE_MIN,
  );

  const handleOpenAllFilters = useCallback(() => {
    openAllFilters({
      showRoomsSection: !roomsShownInline,
      showPriceSection: !priceShownInline,
    });
  }, [openAllFilters, roomsShownInline, priceShownInline]);

  // context `filters` already includes any staged-but-unflushed patch, and
  // `applyFilters` owns the deferred/coalesced apply — see useListingSearchState.

  // When a primary lives in the modal, its active state rolls up into the
  // AllFilters badge instead of its own trigger styling.
  const badgeCount =
    activeFilterCount +
    (roomsShownInline ? 0 : getRoomsFiltersCount(filters)) +
    (priceShownInline ? 0 : getPriceFiltersCount(filters));

  const handleBedroomsChange = useCallback(
    (next: typeof filters.bedrooms) => applyFilters({ bedrooms: next }),
    [applyFilters],
  );
  const handleBathroomsChange = useCallback(
    (next: typeof filters.bathrooms) => applyFilters({ bathrooms: next }),
    [applyFilters],
  );
  const handleRoomsReset = useCallback(
    () =>
      applyFilters({
        bedrooms: { min: null, max: null },
        bathrooms: { min: null },
      }),
    [applyFilters],
  );
  const handlePriceChange = useCallback(
    (next: typeof filters.price) => applyFilters({ price: next }, { immediate: true }),
    [applyFilters],
  );
  const handlePriceReset = useCallback(() => applyFilters({ price: { min: null, max: null } }), [applyFilters]);

  // preventDefault unconditionally: navigation is client-side via submit(),
  // and in live mode (where submit() no-ops) Enter must never reload the SRP.
  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      submit();
    },
    [submit],
  );

  const gap = size === 'sm' ? (compactControls ? 'tightest' : 'default') : 'tighter';

  const roomsTriggerCss =
    size === 'sm'
      ? ({
          display: 'none',
          [`@container listingSearch (min-width: ${SM_ROOMS_INLINE_MIN})`]: {
            display: 'block',
          },
        } as const)
      : ({
          display: 'none',
          [`@container listingSearch (min-width: ${LG_PRIMARY_FILTERS_INLINE_MIN})`]: {
            display: 'block',
          },
        } as const);
  // Two separate objects so Panda statically extracts both threshold rules
  // — a single conditional threshold variable hides the values from
  // extraction and the CSS never lands.
  const priceTriggerCss =
    size === 'sm'
      ? ({
          display: 'none',
          [`@container listingSearch (min-width: ${SM_PRICE_INLINE_MIN})`]: {
            display: 'block',
          },
        } as const)
      : ({
          display: 'none',
          [`@container listingSearch (min-width: ${LG_PRIMARY_FILTERS_INLINE_MIN})`]: {
            display: 'block',
          },
        } as const);

  return (
    <Box ref={containerRef} data-c11n-component="listing-search" data-size={size} className={container}>
      {/* A real <form> so Enter in the location input triggers native implicit
          submission (Constellation's Combobox preventDefaults Enter only while
          its listbox is open, reserving it for option selection). */}
      <Box
        asChild
        role="search"
        aria-label={tFilters('searchFilters')}
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: size === 'sm' && !compactControls ? 'space-between' : undefined,
          gap,
          width: '100%',
          flexWrap: 'nowrap',
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box
            css={{
              // Longhand flex props (Panda doesn't extract the 3-value shorthand
              // with a percentage basis). At sm Location holds ~50% so it stays
              // prominent as primaries stage in. At lg, Location shares one row
              // with the controls while Rooms/Price are either hidden or inline.
              flexGrow: size === 'sm' && !compactControls ? 0 : 1,
              flexShrink: 1,
              flexBasis: size === 'sm' ? (compactControls ? '50%' : SM_HEADER_LOCATION_BASIS) : '0%',
              minWidth: 0,
            }}
          >
            <LocationCombobox
              area={area}
              nearMeArea={nearMeArea}
              onSelect={setArea}
              onEnterSearch={searchArea}
              onUserInput={markUserInteraction}
              onSuggestionsVisibleChange={setSuggestionsVisible}
              size={size}
              compact={compactControls}
            />
          </Box>
          <Box css={roomsTriggerCss}>
            <RoomsDropdown
              bedrooms={filters.bedrooms}
              bathrooms={filters.bathrooms}
              onBedroomsChange={handleBedroomsChange}
              onBathroomsChange={handleBathroomsChange}
              onReset={handleRoomsReset}
              size={size}
            />
          </Box>
          <Box css={priceTriggerCss}>
            <PriceDropdown value={filters.price} onChange={handlePriceChange} onReset={handlePriceReset} size={size} />
          </Box>
          <Box
            data-listing-search-component="trailing-controls"
            // Panda class names are hashed in CI and the stylesheet never
            // loads in jsdom, so the collapse has to be observable as state
            // rather than as a computed style.
            data-collapsed={expandLocationField ? 'true' : 'false'}
            css={{
              // Hidden rather than unmounted so the controls keep their state
              // and reappear without a mount flash when the panel closes.
              display: expandLocationField ? 'none' : 'flex',
              alignItems: 'center',
              gap,
              marginLeft: size === 'sm' && !compactControls ? 0 : 'auto',
            }}
          >
            <AllFiltersButton
              size={size}
              compact={compactControls}
              activeCount={badgeCount}
              onClick={handleOpenAllFilters}
            />
            {trailingAction}
            {size === 'lg' ? <SearchSubmit size={size} disabled={isPending} isPending={isPending} /> : null}
          </Box>
        </form>
      </Box>
    </Box>
  );
}

export default function ListingSearch({ size, mode, nearMeArea, trailingAction, compactControls }: ListingSearchProps) {
  const resolvedMode = mode ?? getDefaultMode(size);

  return (
    <SearchFiltersProvider mode={resolvedMode} nearMeArea={nearMeArea}>
      <ListingSearchControls size={size} trailingAction={trailingAction} compactControls={compactControls} />
    </SearchFiltersProvider>
  );
}
